import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiTag } from 'react-icons/fi';
import SEO from '../SEO/SEO';
import { blogTagUrl } from './blogTagUrl';
import { blogsAPI, getMediaUrl } from '../../services/blogApi';
import { Blog } from '../../types';

const BlogDetail: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!slug) return;

    const fetchBlog = async () => {
      try {
        setLoading(true);
        setError('');
        const data = await blogsAPI.getBySlug(slug);
        setBlog(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load article');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug]);

  if (loading) {
    return <div className="py-20 text-center text-gray-500">Loading article...</div>;
  }

  if (error || !blog) {
    return (
      <div className="py-20 text-center">
        <p className="text-red-500 mb-4">{error || 'Article not found'}</p>
        <Link to="/blog" className="text-blue-600 font-semibold">Back to blog</Link>
      </div>
    );
  }

  const keywords = [blog.primary_keyword, ...blog.secondary_keywords, ...blog.tags].filter(Boolean).join(', ');
  const publishedDate = blog.published_at
    ? new Date(blog.published_at).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null;

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: blog.title,
    description: blog.meta_description,
    image: blog.feature_image_url ? getMediaUrl(blog.feature_image_url) : undefined,
    datePublished: blog.published_at,
    dateModified: blog.updated_at,
    author: {
      '@type': 'Organization',
      name: 'travelbaba786',
    },
    publisher: {
      '@type': 'Organization',
      name: 'travelbaba786',
      logo: {
        '@type': 'ImageObject',
        url: 'https://travelbaba786.com/logo..PNG',
      },
    },
    keywords,
  };

  return (
    <>
      <SEO
        title={blog.meta_title}
        description={blog.meta_description}
        keywords={keywords}
        image={blog.feature_image_url ? getMediaUrl(blog.feature_image_url) : undefined}
        url={`/blog/${blog.slug}`}
        type="article"
        publishedTime={blog.published_at || undefined}
        modifiedTime={blog.updated_at}
        structuredData={structuredData}
      />

      <article className="max-w-4xl mx-auto py-10 px-4">
        <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 mb-6">
          <FiArrowLeft /> Back to blog
        </Link>

        {blog.feature_image_url && (
          <img
            src={getMediaUrl(blog.feature_image_url)}
            alt={blog.feature_image_alt || blog.title}
            className="w-full h-64 md:h-96 object-cover rounded-2xl mb-8"
          />
        )}

        <header className="mb-8">
          <div className="flex flex-wrap gap-2 mb-4">
            {blog.tags.map((tag) => (
              <Link
                key={tag}
                to={blogTagUrl(tag)}
                className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 text-sm font-semibold hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors"
              >
                <FiTag size={12} /> {tag}
              </Link>
            ))}
          </div>

          <h1 className="text-3xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4 leading-tight">
            {blog.title}
          </h1>

          <p className="text-lg text-gray-600 dark:text-gray-300 mb-4">{blog.excerpt}</p>

          {publishedDate && (
            <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
              <FiCalendar />
              Published on {publishedDate}
            </div>
          )}
        </header>

        <div
          className="prose prose-lg dark:prose-invert max-w-none blog-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />
      </article>
    </>
  );
};

export default BlogDetail;
